import { api } from '@/lib/apiClient'
import type { Envelope, PaginatedEnvelope } from '@/lib/apiTypes'
import type { Meta } from '@/lib/pagination'
import { listSearches } from '@/features/searches/api'
import type { AppNotification, NotificationKind, NotificationListing } from './types'

export interface NotificationsPage {
  items: AppNotification[]
  meta: Meta & { unreadCount: number }
}

/** GET /notifications — newest first. Without `perPage`, one single page. */
export async function listNotifications(perPage?: number): Promise<NotificationsPage> {
  const res = await api.get<PaginatedEnvelope<AppNotification> & { meta: NotificationsPage['meta'] }>(
    '/notifications',
    { params: perPage ? { per_page: perPage } : undefined },
  )
  return { items: res.data.data, meta: res.data.meta }
}

/** POST /notifications/:id/read — 404 if not the subscriber's or deleted. */
export async function markNotificationRead(id: number): Promise<void> {
  await api.post(`/notifications/${id}/read`)
}

/** POST /notifications/read-all — returns the count marked read. */
export async function markAllNotificationsRead(): Promise<number> {
  const res = await api.post<Envelope<{ markedRead: number }>>('/notifications/read-all')
  return res.data.data.markedRead
}

/** DELETE /notifications/:id — soft delete, gone from the feed for good. */
export async function deleteNotification(id: number): Promise<void> {
  await api.delete(`/notifications/${id}`)
}

/**
 * GET /notifications/:id/listings — cards behind a notification. Empty for
 * `unseen_reminder` (the click must redirect to /app/biens?seen=false instead).
 */
export async function getNotificationListings(
  id: number,
): Promise<{ kind: NotificationKind; listings: NotificationListing[] }> {
  const res = await api.get<Envelope<{ kind: NotificationKind; listings: NotificationListing[] }>>(
    `/notifications/${id}/listings`,
  )
  return res.data.data
}

/**
 * Badge « biens non vus » du nav : somme des `unseenCount` par zone (biens
 * emailés non encore vus). L'ancien GET /listings/unseen-count est supprimé ;
 * on dérive de la liste des zones, déjà tier-gated côté back.
 */
export async function getUnseenCount(): Promise<number> {
  const { data } = await listSearches()
  return data.reduce((n, s) => n + (s.unseenCount ?? 0), 0)
}
